import { Avatar, Group, MultiSelect, Select, Stack, Text } from '@mantine/core'
import { Grid } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef } from 'react'

import avatars from '@/assets/avatars'
import { theme } from '@/configs/theme'
import { MSK_TOPIC } from '@/constants/contentModules'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { getDefaultPskAvatar } from '@/screens/SetupScreen/utils'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { getFieldDisplayName, getRequiredFieldDescriptionProps } from '@/utils/forms'

export const TopicSettingsSection = observer(function TopicSettingsSection(props: CMFormSection) {
  const { authenticationStore } = useGlobalState()
  const { user } = authenticationStore

  // Field refs for granular scrolling
  const fieldRefs = {
    selectEsk: useRef<HTMLInputElement>(null),
    categories: useRef<HTMLInputElement>(null),
    mappings: useRef<HTMLInputElement>(null),
  }

  // Expose field refs through props if available
  useEffect(() => {
    if (props.setFieldRefs) {
      props.setFieldRefs(fieldRefs as Record<string, React.RefObject<HTMLElement>>)
    }
  }, [fieldRefs, props.setFieldRefs])

  const topic = props.moduleForm.getValues().categories[0]
  const topicOptions = useMemo(() => {
    let options = topicOptionsMapper(props.topicsQuery.data ?? [])
    if (user?.isSponsorAdmin && props.moduleForm.values.visibleToMsk) {
      options = [
        {
          value: MSK_TOPIC,
          label: MSK_TOPIC,
        },
      ]
    }

    return options
  }, [props.topicsQuery.data, props.moduleForm.values.visibleToMsk])

  const subTopics = useMemo(() => {
    const t = props.topicsQuery.data?.find(t => t.displayName === topic)
    if (!t) return []

    return topicOptionsMapper(t.subtopics)
  }, [topic])

  const skAvatar = useMemo(() => {
    // If this is a personal sidekick (visibleToMsk), use the default PSK avatar
    if (props.moduleForm.values.visibleToMsk) {
      return getDefaultPskAvatar(props.sidekicksQuery.data)
    }

    // Get the selected topic and its skId
    const selectedTopicName = props.moduleForm.getValues().categories[0]
    const selectedTopic = props.topicsQuery.data?.find(t => t.displayName === selectedTopicName)

    if (!selectedTopic?.skId) return avatars.placeholder

    // Handle PSK case
    if (selectedTopic.skId === 'PSK') {
      return getDefaultPskAvatar(props.sidekicksQuery.data)
    }

    // Find ESK by the topic's skId
    if (!props.esksQuery.data) return avatars.placeholder
    const esk = props.esksQuery.data.find(e => e.eskId === selectedTopic.skId)

    return esk ? avatars[esk.avatarId] : avatars.placeholder
  }, [
    props.moduleForm.values.visibleToMsk,
    props.moduleForm.getValues().categories,
    props.topicsQuery.data,
    props.esksQuery.data,
    props.sidekicksQuery.data,
  ])

  const hasSubTopics = subTopics.length > 0

  return (
    <Grid gutter="xxxl">
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Select
          ref={fieldRefs.categories}
          label={getFieldDisplayName('categories')}
          withAsterisk
          styles={{
            label: {
              fontSize: '15px',
            },
          }}
          clearable={!props.moduleForm.values.visibleToMsk}
          data={topicOptions}
          {...props.moduleForm.getInputProps('categories')}
          value={props.moduleForm.values.visibleToMsk ? MSK_TOPIC : (topic ?? null)}
          {...(!props.moduleForm.values.visibleToMsk
            ? getRequiredFieldDescriptionProps(props.moduleForm, 'categories', '')
            : {})}
          onChange={v => {
            props.moduleForm.setFieldValue('mappings', [])

            const topic = props.topicsQuery.data?.find(t => t.displayName === v)

            if (!v || !topic) {
              if (props.moduleForm.values.visibleToMsk) {
                props.moduleForm.setFieldValue('categories', [MSK_TOPIC])
              } else {
                props.moduleForm.setFieldValue('categories', [])
                props.moduleForm.setFieldValue('selectEsk', '')
              }
            } else {
              props.moduleForm.setFieldValue('categories', [topic.displayName])

              // Use the topic's skId to set the selectEsk field
              if (topic.skId) {
                if (topic.skId === 'PSK') {
                  // For PSK, we might need to handle this differently
                  // For now, set it to PSK since PSK doesn't have an eskId
                  props.moduleForm.setFieldValue('selectEsk', 'PSK')
                } else {
                  props.moduleForm.setFieldValue('selectEsk', topic.skId)
                }
              } else {
                props.moduleForm.setFieldValue('selectEsk', '')
              }
            }
          }}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 4 }}>
        {!props.moduleForm.values.visibleToMsk && (
          <MultiSelect
            ref={fieldRefs.mappings}
            label={getFieldDisplayName('mappings')}
            styles={{
              label: {
                fontSize: '15px',
              },
            }}
            data={subTopics}
            disabled={!hasSubTopics}
            {...props.moduleForm.getInputProps('mappings')}
            {...getRequiredFieldDescriptionProps(props.moduleForm, 'mappings', '')}
          />
        )}
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 4 }}>
        <Group gap="lg">
          <Avatar
            variant="transparent"
            className={theme.cx(classes.eskAvatarBase, !!skAvatar && classes.eskAvatar)}
            radius="sm"
            size="lg"
            src={skAvatar}
          />
          <Stack gap={4} flex={1} h={120}>
            <Text style={{ fontSize: '15px' }}>Sidekick</Text>
            <Text size="lg" fw={500}>
              {props.moduleForm.values.visibleToMsk
                ? 'Personal Sidekick'
                : (() => {
                    const selectedTopicName = props.moduleForm.getValues().categories[0]
                    const selectedTopic = props.topicsQuery.data?.find(t => t.displayName === selectedTopicName)

                    if (!selectedTopic?.skId) return ''

                    // Handle PSK case
                    if (selectedTopic.skId === 'PSK') {
                      return 'Personal Sidekick'
                    }

                    // Find ESK by the topic's skId
                    return props.esksQuery.data?.find(e => e.eskId === selectedTopic.skId)?.name ?? ''
                  })()}
            </Text>
          </Stack>
        </Group>
      </Grid.Col>
    </Grid>
  )
})

function topicOptionsMapper(data: { displayName: string }[]) {
  return data.map(c => ({ value: c.displayName, label: c.displayName })).filter(c => !!c.label)
}
